import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiTarget, FiColumns, FiGift, FiStar, FiLayers } = FiIcons;

const WinnerToasts = ({ entries }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Filter out non-winning entries
  const winningEntries = entries.filter(e => !e.reward.includes('Luck') && !e.reward.includes('Try'));

  useEffect(() => {
    if (winningEntries.length === 0) return;

    const cycleToast = () => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % winningEntries.length);
        setIsVisible(true);
      }, 800); // Wait 800ms before showing the next one
    };

    setIsVisible(true);
    const interval = setInterval(cycleToast, 7000); // Show each toast for 7 seconds

    return () => clearInterval(interval);
  }, [winningEntries.length]);

  if (winningEntries.length === 0) return null;

  const currentEntry = winningEntries[currentIndex];
  if (!currentEntry) return null;

  // Determine flavor text and icon based on game type
  let tag = "Recent Winner";
  let icon = FiAward;
  let iconColor = "text-emerald-500 bg-emerald-500/20";
  let borderColor = "border-emerald-500/30";

  if (currentEntry.gameType === 'Spin Wheel') {
    tag = "Lucky Spinner"; icon = FiTarget; iconColor = "text-blue-500 bg-blue-500/20"; borderColor = "border-blue-500/30";
  } else if (currentEntry.gameType === 'Slot Machine') {
    tag = "Jackpot Hitter"; icon = FiColumns; iconColor = "text-red-500 bg-red-500/20"; borderColor = "border-red-500/30";
  } else if (currentEntry.gameType === 'Peel & Reveal') {
    tag = "Peel Master"; icon = FiLayers; iconColor = "text-purple-500 bg-purple-500/20"; borderColor = "border-purple-500/30";
  } else if (currentEntry.gameType === 'Scratch Card') {
    tag = "Lucky Scratcher"; icon = FiGift; iconColor = "text-orange-500 bg-orange-500/20"; borderColor = "border-orange-500/30";
  } else if (currentEntry.gameType === 'Flip Card') {
    tag = "Card Flipper"; icon = FiAward; iconColor = "text-emerald-500 bg-emerald-500/20"; borderColor = "border-emerald-500/30";
  } else if (currentEntry.gameType === 'Balloon Pop') {
    tag = "Balloon Popper 🎈"; icon = FiAward; iconColor = "text-pink-500 bg-pink-500/20"; borderColor = "border-pink-500/30";
  } else if (currentEntry.gameType === 'Treasure Box' || currentEntry.gameType === 'Magic Treasure Box') {
    tag = "Treasure Hunter 🎁"; icon = FiGift; iconColor = "text-amber-500 bg-amber-500/20"; borderColor = "border-amber-500/30";
  } else if (currentEntry.gameType === 'Astro-Engineer Rocket') {
    tag = "Rocket Scientist 🚀"; icon = FiAward; iconColor = "text-cyan-500 bg-cyan-500/20"; borderColor = "border-cyan-500/30";
  } else if (currentEntry.gameType === 'Cosmic Gravity Lab') {
    tag = "Astrophysicist ⚖️"; icon = FiAward; iconColor = "text-amber-500 bg-amber-500/20"; borderColor = "border-amber-500/30";
  } else if (currentEntry.gameType === 'Magic Color Lab') {
    tag = "Wonder Chemist 🧪"; icon = FiGift; iconColor = "text-purple-500 bg-purple-500/20"; borderColor = "border-purple-500/30";
  } else if (currentEntry.gameType === 'Candy Piñata') {
    tag = "Piñata Smasher 🪅"; icon = FiAward; iconColor = "text-pink-500 bg-pink-500/20"; borderColor = "border-pink-500/30";
  } else if (currentEntry.gameType === 'Animal Safari Match') {
    tag = "Safari Explorer 🦁"; icon = FiAward; iconColor = "text-emerald-500 bg-emerald-500/20"; borderColor = "border-emerald-500/30";
  } else if (currentEntry.gameType === 'Rainbow Bubble Pop') {
    tag = "Bubble Wizard 🫧"; icon = FiStar; iconColor = "text-sky-500 bg-sky-500/20"; borderColor = "border-sky-500/30";
  } else if (currentEntry.gameType === 'Solar System Planetary Orrery' || currentEntry.gameType?.includes('Solar System')) {
    tag = "Galactic Navigator 🪐"; icon = FiStar; iconColor = "text-amber-500 bg-amber-500/20"; borderColor = "border-amber-500/30";
  } else if (currentEntry.gameType === 'Dino Fossil Dig & Paleontologist Lab' || currentEntry.gameType?.includes('Dino Fossil')) {
    tag = "Chief Paleontologist 🦖"; icon = FiAward; iconColor = "text-amber-600 bg-amber-600/20"; borderColor = "border-amber-600/30";
  }

  // Every 3rd index gets an "Hourly Star" tag for extra hype
  if (currentIndex % 3 === 0) {
    tag = "Hourly Star 🌟"; icon = FiStar; iconColor = "text-amber-500 bg-amber-500/20"; borderColor = "border-amber-500/30";
  }

  return (
    <div className="fixed bottom-20 left-6 z-[60] pointer-events-none">
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, x: -50, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className={`glass-panel bg-white/90 dark:bg-slate-900/90 border ${borderColor} rounded-2xl p-4 shadow-2xl flex items-center gap-4 max-w-sm`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${iconColor}`}>
              <SafeIcon icon={icon} className="text-2xl" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-0.5">
                {tag}
              </div>
              <div className="text-sm font-bold text-slate-800 dark:text-white leading-tight">
                {currentEntry.name} <span className="font-normal text-slate-600 dark:text-slate-300">just won</span>
              </div>
              <div className="text-sm font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500 truncate max-w-[200px]">
                {currentEntry.reward}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WinnerToasts;